import { useMemo, useCallback, useState } from 'react';
import { useGetHealthTopicsQuery } from 'services/resourceService';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';
import { HEALTH_TOPIC_METADATA } from '../constants';
import { TransformedHealthTopic, TransformedLibraryArticle } from '../types';

export const useResources = () => {
  const user = useLoggedInUser();
  const { trackEvent } = usePendo();
  const memberUuid = user?.uuid;
  const shouldSkip = !memberUuid;

  const [alertState, setAlertState] = useState<{
    isPresented: boolean;
    message: string;
  }>({
    isPresented: false,
    message: '',
  });

  const [activeArticle, setActiveArticle] = useState<TransformedLibraryArticle | null>(null);

  const setAlertMessage = useCallback((message: string) => {
    setAlertState({ isPresented: !!message, message });
  }, []);

  const dismissAlert = useCallback(() => {
    setAlertState((prev) => ({ ...prev, isPresented: false }));
  }, []);
  const {
    data: healthTopicsData,
    isLoading: isLoadingHealthTopics,
    error: healthTopicsError,
    refetch: refetchHealthTopics,
  } = useGetHealthTopicsQuery({ memberUuid: memberUuid! }, { skip: shouldSkip });

  const transformedHealthTopics = useMemo<TransformedHealthTopic[]>(() => {
    if (!healthTopicsData) {
      return [];
    }

    return healthTopicsData
      .map((topic) => {
        const metadata = HEALTH_TOPIC_METADATA[topic.identifier];

        if (!metadata) {
          console.warn(`No metadata found for health topic: ${topic.identifier}`);
          return null;
        }

        return {
          identifier: topic.identifier,
          healthTopic: metadata.healthTopic,
          displayName: metadata.displayName,
          iconName: metadata.iconName,
        };
      })
      .filter((topic): topic is TransformedHealthTopic => topic !== null);
  }, [healthTopicsData]);

  const handleArticleClick = useCallback(
    (article: TransformedLibraryArticle) => {
      trackEvent(PendoEventType.SPECIFIC_ARTICLE, {
        eventType: PendoEventType.SPECIFIC_ARTICLE,
        articleId: `${article.id}`,
      });

      setActiveArticle(article);
    },
    [trackEvent],
  );

  const handleModalClose = useCallback(() => {
    setActiveArticle(null);
  }, []);

  return {
    healthTopics: transformedHealthTopics,
    isLoadingHealthTopics,
    healthTopicsError,
    refetchHealthTopics,
    handleArticleClick,
    activeArticle,
    handleModalClose,
    alertState,
    setAlertMessage,
    dismissAlert,
  };
};
